export interface ReviewFaq {
  question: string
  answer: string
}

export interface ReviewProduct {
  slug: string
  name: string
  rating: number
  reviews: number
  price: string
  image: string
  tagline: string
  category: string
  benefits: string[]
  keyFeatures: string[]
  overview: string
  dosage: {
    amount: string
    timing: string
    notes: string[]
  }
  whoItsFor: string[]
  whoShouldAvoid: string[]
  faqs: ReviewFaq[]
  relatedSlugs: string[]
  relatedComparisonSlug?: string
  affiliateUrl: string
  seoTitle: string
  seoDescription: string
}

export const reviewProducts: ReviewProduct[] = [
  {
    slug: "b-vital-total",
    name: "B Vital Total",
    rating: 4.8,
    reviews: 1247,
    price: "$24.99",
    image: "https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?w=400&h=400&fit=crop",
    tagline: "Complete B-Complex for Energy & Mental Clarity",
    category: "Energy",
    benefits: [
      "8 Essential B vitamins in one formula",
      "Supports energy metabolism",
      "Reduces fatigue and tiredness",
      "Promotes healthy nervous system"
    ],
    keyFeatures: [
      "High bioavailability forms",
      "Vegan-friendly capsules",
      "Third-party tested",
      "No artificial colors or preservatives"
    ],
    overview:
      "B Vital Total is a complete B-complex designed for people who want steadier daytime energy without relying on caffeine. Vitamin Vital Portal reviewers look for methylated B12 and folate, transparent dosing, and a clean capsule formula. This review covers benefits, quality checks, and a practical dosage guide so you can compare it with other vitality supplements before checking the best price.",
    dosage: {
      amount: "2 capsules daily",
      timing: "Take with breakfast or your first meal",
      notes: [
        "Most users notice energy support after 1 to 2 weeks of consistent use.",
        "Take with food if you have a sensitive stomach.",
        "Do not exceed the labeled dose unless a clinician advises otherwise."
      ]
    },
    whoItsFor: [
      "Adults with fatigue or afternoon energy crashes",
      "People comparing B Vital against lower-dose B complexes",
      "Anyone who prefers methylated B12 and folate"
    ],
    whoShouldAvoid: [
      "Anyone already taking a high-dose B-complex unless a doctor reviews the stack",
      "People with a diagnosed B-vitamin disorder who need medical dosing"
    ],
    faqs: [
      {
        question: "How long does B Vital Total take to work?",
        answer: "Many people feel a difference in daily energy within 7 to 14 days. Nervous-system and mood support can take 4 to 6 weeks of consistent use."
      },
      {
        question: "What is the best time of day to take B Vital Total?",
        answer: "Morning with food is the usual recommendation because B vitamins support energy metabolism and can feel stimulating later in the day."
      },
      {
        question: "Is B Vital Total better than Arko Vital for energy?",
        answer: "B Vital Total uses higher potencies and methylated forms. For a full side-by-side, see the Arko Vital vs. B Vital comparison guide."
      }
    ],
    relatedSlugs: ["d-vital-10000", "immunovit-plus"],
    relatedComparisonSlug: "arko-vital-vs-b-vital",
    affiliateUrl: "#",
    seoTitle: "B Vital Total Review & Dosage Guide | Vitamin Vital Portal",
    seoDescription: "Read the Vitamin Vital Portal review of B Vital Total, including dosage, benefits, and who it is for. Compare energy supplements and check the best price."
  },
  {
    slug: "d-vital-10000",
    name: "D Vital 10,000",
    rating: 4.9,
    reviews: 2134,
    price: "$19.99",
    image: "https://images.unsplash.com/photo-1550572017-edd951aa8f72?w=400&h=400&fit=crop",
    tagline: "High-Potency Vitamin D3 for Immunity & Bone Health",
    category: "Vitamin D",
    benefits: [
      "10,000 IU per softgel",
      "Supports immune function",
      "Promotes calcium absorption",
      "Maintains healthy bones and teeth"
    ],
    keyFeatures: [
      "Cholecalciferol (D3) form",
      "Extra virgin olive oil base",
      "Non-GMO certified",
      "Easy-to-swallow softgels"
    ],
    overview:
      "D Vital 10,000 is a high-potency Vitamin D3 option for adults with low or insufficient blood levels, limited sun exposure, or immunity goals. Vitamin Vital Portal evaluates D3 form, oil base, and dose clarity because 10,000 IU is stronger than a typical daily multivitamin. Use this review with your lab results and a clinician before changing your dose.",
    dosage: {
      amount: "1 softgel daily, or as directed by a clinician",
      timing: "Take with a meal that contains fat for better absorption",
      notes: [
        "10,000 IU is a high dose. Confirm need with a Vitamin D blood test when possible.",
        "People with levels under 20 ng/mL should speak with a doctor before long-term use.",
        "Do not combine with other high-dose Vitamin D products without medical guidance."
      ]
    },
    whoItsFor: [
      "Adults with low or insufficient Vitamin D lab results",
      "People seeking immune and bone-health support",
      "Users who prefer D3 in an oil-based softgel"
    ],
    whoShouldAvoid: [
      "Anyone with a history of high calcium or Vitamin D excess",
      "People already taking prescription Vitamin D unless a clinician approves"
    ],
    faqs: [
      {
        question: "Is D Vital 10,000 too strong for daily use?",
        answer: "It is a high-potency formula. Many adults use it short term under guidance, then retest. The Vitamin Vitality Calculator can flag low reported levels, but it is not a lab test."
      },
      {
        question: "Can I take D Vital 10,000 with B Vital Total?",
        answer: "They target different needs and are commonly stacked. Take D Vital with a fat-containing meal and B Vital in the morning unless your clinician suggests another plan."
      },
      {
        question: "What Vitamin D level is considered low?",
        answer: "Vitamin Vital Portal treats under 30 ng/mL as below optimal in the calculator, and under 20 ng/mL as a deficiency-range flag that should be reviewed with a doctor."
      }
    ],
    relatedSlugs: ["immunovit-plus", "b-vital-total"],
    affiliateUrl: "#",
    seoTitle: "D Vital 10,000 Review & Dosage Guide | Vitamin Vital Portal",
    seoDescription: "Expert D Vital 10,000 review covering dosage, immunity benefits, and Vitamin D blood-level context. Compare options on Vitamin Vital Portal and check the best price."
  },
  {
    slug: "immunovit-plus",
    name: "Immunovit Plus",
    rating: 4.7,
    reviews: 892,
    price: "$29.99",
    image: "https://images.unsplash.com/photo-1607619056574-7b8d3ee536b2?w=400&h=400&fit=crop",
    tagline: "Advanced Immune Support with Vitamin C, Zinc & Elderberry",
    category: "Immunity",
    benefits: [
      "1000mg Vitamin C per serving",
      "Zinc for immune cell function",
      "Elderberry extract (10:1 concentration)",
      "Echinacea & probiotics included"
    ],
    keyFeatures: [
      "Triple-action immune formula",
      "Buffered Vitamin C (gentle on stomach)",
      "Gluten-free and allergen-free",
      "Manufactured in GMP facility"
    ],
    overview:
      "Immunovit Plus is a daily immune formula combining Vitamin C, zinc, elderberry, and supporting botanicals. Vitamin Vital Portal positions it for users who want broader defense support than Vitamin D alone, including hair, skin, and general wellness goals. This review explains the formula, dosage, and how it compares with targeted energy or D3 products.",
    dosage: {
      amount: "1 serving daily",
      timing: "Take with a meal to improve comfort and consistency",
      notes: [
        "Buffered Vitamin C is included to be gentler on the stomach.",
        "If you already take a standalone zinc or Vitamin C product, review total daily amounts.",
        "Stop and seek medical advice if you notice unusual digestive discomfort."
      ]
    },
    whoItsFor: [
      "Adults looking for daily immune support",
      "Users with hair, skin, and nail goals alongside wellness",
      "People who want Vitamin C and zinc in one formula"
    ],
    whoShouldAvoid: [
      "Anyone with a zinc, Vitamin C, or botanical allergy listed on the label",
      "People on immune-modulating medication without clinician approval"
    ],
    faqs: [
      {
        question: "Is Immunovit Plus a replacement for D Vital 10,000?",
        answer: "No. Immunovit Plus covers Vitamin C, zinc, and botanicals. Vitamin D3 is a separate need, especially if blood levels are low."
      },
      {
        question: "Can Immunovit Plus help with hair health?",
        answer: "It is recommended in the Vitality Calculator when hair health is selected because the formula supports broader nutrient intake. Results vary and it is not a medical hair-loss treatment."
      },
      {
        question: "When should I take Immunovit Plus?",
        answer: "With a meal is the simplest routine. If you also take B Vital Total, keep B vitamins earlier in the day and this formula with food."
      }
    ],
    relatedSlugs: ["d-vital-10000", "b-vital-total"],
    affiliateUrl: "#",
    seoTitle: "Immunovit Plus Review & Dosage Guide | Vitamin Vital Portal",
    seoDescription: "Immunovit Plus review with dosage, immune-support ingredients, and who it is for. Compare vitality supplements on Vitamin Vital Portal and check the best price."
  }
]

export function getReviewBySlug(slug: string | undefined): ReviewProduct | undefined {
  if (!slug) return undefined
  return reviewProducts.find((product) => product.slug === slug)
}

export function getRelatedReviews(product: ReviewProduct): ReviewProduct[] {
  return product.relatedSlugs
    .map((slug) => getReviewBySlug(slug))
    .filter((item): item is ReviewProduct => Boolean(item))
}
